import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getPageSEO,
  LOCALIZED_SEO_PREFIX,
  type PageSEOKey,
} from "./pageSEO";
import { canonicalPathFor } from "../../router/routeSeo";
import {
  applySEO,
  resolveSEO,
  toCanonical,
  type RobotsDirective,
  type SEOProps,
} from "./applySEO";


export interface PageSEOOptions {
  /** Override the canonical URL. Defaults to the current pathname. */
  canonical?: string;
  robots?: RobotsDirective;
  /** Override the config title, e.g. with a loaded record's name. */
  title?: string;
  /** Override the config description. */
  description?: string;
  /** Override the config og:image with a record-specific image. */
  image?: string;
}

export const useSeoProps = (
  page: PageSEOKey,
  options?: PageSEOOptions,
): SEOProps | null => {
  const { pathname } = useLocation();
  const config = getPageSEO(page);

  const { canonical, robots, title, description, image } = options ?? {};

  return useMemo(() => {
    if (!config) return null;
    return {
      title: title ?? config.title,
      description: description ?? config.description,
      keywords: config.keywords,
      image: image ?? config.ogImage,
      type: config.ogType,
      // Canonical follows the URL actually being viewed, so detail pages
      // under a shared page config do not all claim the list page.
      // canonicalPathFor resolves the handful of routes that serve identical
      // content to the URL that owns it.
      canonical: toCanonical(canonical ?? canonicalPathFor(pathname)),
      robots,
    };
  }, [config, pathname, canonical, robots, title, description, image]);
};

export function usePageSEO(page: PageSEOKey, options?: PageSEOOptions) {
  const { t, i18n } = useTranslation();
  const props = useSeoProps(page, options);
  const language = i18n.language;

  useEffect(() => {
    if (!props) return;

    const prefix = LOCALIZED_SEO_PREFIX[page];
    // Same translated title/description the server rendered for /ru and /uz
    // (pages.seoMeta, see src/seo/routeMeta.ts), so this effect doesn't
    // replace them with the English ones.
    const seoMeta = `pages.seoMeta.${page}`;
    const translated = !prefix && i18n.exists(`${seoMeta}.title`);
    applySEO(
      resolveSEO(
        prefix
          ? {
              ...props,
              title: options?.title ?? t(`${prefix}.title`),
              description: options?.description ?? t(`${prefix}.description`),
              keywords: t(`${prefix}.keywords`),
            }
          : translated
            ? {
                ...props,
                title: options?.title ?? t(`${seoMeta}.title`),
                description: options?.description ?? t(`${seoMeta}.description`),
              }
            : props,
      ),
    );
    // `language` is in the dep list so switching locale re-applies metadata.
  }, [props, page, t, i18n, language, options?.title, options?.description]);
}
